if (!customElements.get("subscription-quick-add")) {
  class SubscriptionQuickAdd extends HTMLElement {
    static openDropdowns = new Set();
    static globalListenerAttached = false;
    static igProductCache = new Map();

    static fetchTestProduct(handle) {
      if (!SubscriptionQuickAdd.igProductCache.has(handle)) {
        SubscriptionQuickAdd.igProductCache.set(
          handle,
          fetch(`/products/${handle}.js`)
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null)
        );
      }
      return SubscriptionQuickAdd.igProductCache.get(handle);
    }

    constructor() {
      super();
      this.triggerBtn = this.querySelector(".product-card__action-button");
      this.content = this.querySelector(".subscription-quick-add-content");
      this.container = this.querySelector(".subscription-quick-add-container");
      this.form = this.querySelector(".subscription-quick-add__form");
      this.submitBtn = this.querySelector(".subscription-submit-btn");

      this.activateDropdown = this.activateDropdown.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);
      this.handlePlanUpdate = this.handlePlanUpdate.bind(this);
      this.isClosed = true;

      SubscriptionQuickAdd.attachGlobalListener();
    }

    static attachGlobalListener() {
      if (!SubscriptionQuickAdd.globalListenerAttached) {
        document.addEventListener(
          "click",
          SubscriptionQuickAdd.handleGlobalClick
        );
        SubscriptionQuickAdd.globalListenerAttached = true;
      }
    }

    static handleGlobalClick(event) {
      SubscriptionQuickAdd.openDropdowns.forEach((dropdown) => {
        if (!dropdown.contains(event.target)) {
          dropdown.closeDropdown();
        }
      });
    }

    connectedCallback() {
      this.triggerBtn.addEventListener("click", this.activateDropdown);
      if (this.form) {
        this.form.addEventListener("submit", this.handleFormSubmit);
      }

      this.submitBtnLabel = this.submitBtn?.querySelector(
        ".subscription-submit-btn__label"
      );

      this.addEventListener("skio::update-selling-plan", this.handlePlanUpdate);
    }

    async setProduct(handle) {
      if (!handle) return;
      const product = await SubscriptionQuickAdd.fetchTestProduct(handle);
      if (!product || !product.variants || !product.variants.length) return;
      await this.swapProduct(product);
    }

    async swapProduct(product) {
      await customElements.whenDefined("skio-plan-picker");
      const picker = this.querySelector("skio-plan-picker");
      if (!picker) return;
      if (picker.productHandle === product.handle) {
        this.setAttribute("data-ig-swapped", "");
        return;
      }
      const variant = product.variants[0];
      picker.productHandle = product.handle;
      this.dataset.productId = product.id;
      this.dataset.variantId = variant.id;
      this.querySelectorAll('input[name="id"]').forEach((input) => {
        input.value = variant.id;
        input.disabled = !variant.available;
      });
      if (typeof picker.lockToProduct === "function") {
        picker.lockToProduct(product);
      } else {
        picker._igLocked = true;
        picker.product = product;
        picker.selectedVariant = variant;
      }
      if (picker.updateComplete) await picker.updateComplete;
      this.setAttribute("data-ig-swapped", "");
    }

    disconnectedCallback() {
      this.triggerBtn.removeEventListener("click", this.activateDropdown);
      if (this.form) {
        this.form.removeEventListener("submit", this.handleFormSubmit);
      }
      SubscriptionQuickAdd.openDropdowns.delete(this);
      this.removeEventListener(
        "skio::update-selling-plan",
        this.handlePlanUpdate
      );
    }

    handlePlanUpdate(event) {
      if (!event.detail || !event.detail.formattedPrice) return;

      const { sellingPlan, formattedPrice, formattedFullPrice } = event.detail;
      const hasDiscount =
        sellingPlan && formattedFullPrice && formattedFullPrice !== formattedPrice;
      // Intelligems price tagging: this component overwrites the button price on
      // every selling-plan change, which strips the ig* classes added in Liquid.
      // Re-emit them here so Intelligems can detect/replace the price each time.
      // - Subscription price -> igPrice with a data-price-multiplier derived from
      //   the plan discount (displayed = base price x ratio).
      // - Full/one-time price (the <s>) -> igPrice (the base price, multiplier 1).
      const pid = this.dataset.productId || "";
      const vid = this.dataset.variantId || "";
      const idAttrs = `data-product-id="${pid}" data-variant-id="${vid}"`;
      const toNum = (s) => parseFloat(String(s).replace(/[^0-9.]/g, "")) || 0;
      let multAttr = "";
      if (hasDiscount) {
        const ratio = toNum(formattedFullPrice)
          ? toNum(formattedPrice) / toNum(formattedFullPrice)
          : 0;
        if (ratio > 0 && ratio < 1) {
          multAttr = ` data-price-multiplier="${ratio.toFixed(4)}"`;
        }
      }
      const priceHtml = `<span class="igPrice" ${idAttrs}${multAttr}>${formattedPrice}</span>`;
      const html = hasDiscount
        ? `ADD ${priceHtml} <s class="igPrice" ${idAttrs}>${formattedFullPrice}</s>`
        : `ADD ${priceHtml}`;

      if (this.submitBtnLabel) {
        this.submitBtnLabel.innerHTML = html;
      } else if (this.submitBtn) {
        this.submitBtn.innerHTML = html;
      }
      this.triggerBtn.innerHTML = html;
    }

    activateDropdown(event) {
      event.stopPropagation();

      if (this.isClosed) {
        SubscriptionQuickAdd.openDropdowns.forEach((dropdown) => {
          if (dropdown !== this) {
            dropdown.closeDropdown();
          }
        });

        this.triggerBtn.classList.add("subscription-dropdown-active");
        this.content.style.maxHeight = this.container.scrollHeight + 4 + "px";
        const onOpenEnd = (e) => {
          if (e.propertyName !== "max-height") return;
          this.content.removeEventListener("transitionend", onOpenEnd);
          if (!this.isClosed) this.content.style.maxHeight = "unset";
        };
        this.content.addEventListener("transitionend", onOpenEnd);
        this.isClosed = false;

        SubscriptionQuickAdd.openDropdowns.add(this);
      } else {
        this.closeDropdown();
      }
    }

    closeDropdown() {
      this.content.style.maxHeight = this.content.scrollHeight + "px";
      this.content.offsetHeight;
      this.content.style.maxHeight = "0px";

      const onCloseEnd = (e) => {
        if (e.propertyName !== "max-height") return;
        this.content.removeEventListener("transitionend", onCloseEnd);
        if (this.isClosed) this.triggerBtn.classList.remove("subscription-dropdown-active");
      };
      this.content.addEventListener("transitionend", onCloseEnd);

      this.isClosed = true;
      SubscriptionQuickAdd.openDropdowns.delete(this);
    }

    async handleFormSubmit(event) {
      event.preventDefault();

      if (this.submitBtn) {
        this.submitBtn.disabled = true;
        this.submitBtn.classList.add("btn--loading");
      }

      try {
        const formData = new FormData(this.form);
        const item = {
          id: formData.get("id"),
          quantity: 1,
        };

        const sellingPlan = formData.get("selling_plan");
        if (sellingPlan && sellingPlan !== "") {
          item.selling_plan = sellingPlan;
        }

        const properties = {};
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && value !== "") properties[match[1]] = value;
        }
        if (Object.keys(properties).length > 0) item.properties = properties;

        const productData = { items: [item] };

        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(productData),
        });

        if (response.ok) {
          const addResult = await response.clone().json().catch(() => null);
          this.closeDropdown();

          try {
            const cartResponse = await fetch(`${FoxTheme.routes.cart_url}`, {
              ...FoxTheme.utils.fetchConfig(),
            });
            const cartJson = await cartResponse.json();
            FoxTheme.pubsub.publish(
              FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
              { cart: cartJson }
            );
          } catch (error) {
            console.error("Error fetching cart state:", error);
          }

          const addedLine =
            addResult && (addResult.items ? addResult.items[0] : addResult);
          if (addedLine) {
            document.dispatchEvent(
              new CustomEvent("product-ajax:added", {
                bubbles: true,
                detail: { product: addedLine },
              })
            );
          }

          const cartDrawer = document.querySelector("cart-drawer");
          if (cartDrawer) {
            document.dispatchEvent(
              new CustomEvent("cart:refresh", {
                detail: { open: true },
              })
            );
          } else {
            window.location.href = "/cart";
          }
        } else {
          console.error("Failed to add product to cart");
          this.form.submit();
        }
      } catch (error) {
        console.error("Error adding product to cart:", error);
        this.form.submit();
      } finally {
        if (this.submitBtn) {
          this.submitBtn.disabled = false;
          this.submitBtn.classList.remove("btn--loading");
        }
      }
    }
  }

  customElements.define("subscription-quick-add", SubscriptionQuickAdd);
}
